import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const ReviewsCarousel = ({ reviews }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const intervalRef = useRef(null);

    // Auto-slide functionality
    useEffect(() => {
        if (reviews.length <= 1 || isPaused) return;

        intervalRef.current = setInterval(() => {
            setCurrentIndex((prev) => (prev + 1) % reviews.length);
        }, 5000); // Change review every 5 seconds

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [reviews.length, isPaused]);

    const goToNext = () => {
        setCurrentIndex((prev) => (prev + 1) % reviews.length);
    };

    const goToPrevious = () => {
        setCurrentIndex((prev) => (prev === 0 ? reviews.length - 1 : prev - 1));
    };

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    if (!reviews || reviews.length === 0) {
        return null;
    }

    const renderStars = (rating) => {
        return [...Array(5)].map((_, i) => (
            <Star
                key={i}
                size={20}
                className={i < rating ? 'fill-amber-400 text-amber-400' : 'text-soft-gray fill-transparent'}
            />
        ));
    };

    return (
        <div
            className="relative"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
        >
            {/* Reviews Container */}
            <div className="relative min-h-[280px] overflow-hidden">
                {reviews.map((review, index) => (
                    <div
                        key={review.id}
                        className={`absolute inset-0 transition-all duration-700 ${index === currentIndex
                                ? 'opacity-100 translate-x-0'
                                : index < currentIndex
                                    ? 'opacity-0 -translate-x-full'
                                    : 'opacity-0 translate-x-full'
                            }`}
                    >
                        <div className="bg-cream-white rounded-lg p-8 shadow-lg border border-soft-gray h-full flex flex-col justify-between">
                            {/* Stars */}
                            <div className="flex gap-1 mb-4">
                                {renderStars(review.rating)}
                            </div>

                            {/* Review Text */}
                            <p className="text-charcoal text-lg leading-relaxed mb-6 flex-grow">
                                "{review.review_text}"
                            </p>

                            {/* Author Info */}
                            <div className="border-t border-soft-gray pt-4">
                                <p className="font-medium text-charcoal">{review.customer_name}</p>
                                {review.workshop_attended && (
                                    <p className="text-sm text-warm-gray mt-1">{review.workshop_attended}</p>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {/* Navigation Arrows */}
            {reviews.length > 1 && (
                <>
                    <button
                        onClick={goToPrevious}
                        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 p-3 rounded-full bg-cream-white shadow-lg border border-soft-gray hover:bg-warm-sage hover:text-cream-white transition-all duration-300 z-10"
                        aria-label="Previous review"
                    >
                        <ChevronLeft size={24} />
                    </button>
                    <button
                        onClick={goToNext}
                        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 p-3 rounded-full bg-cream-white shadow-lg border border-soft-gray hover:bg-warm-sage hover:text-cream-white transition-all duration-300 z-10"
                        aria-label="Next review"
                    >
                        <ChevronRight size={24} />
                    </button>
                </>
            )}

            {/* Navigation Dots */}
            {reviews.length > 1 && (
                <div className="flex justify-center gap-2 mt-8">
                    {reviews.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`transition-all duration-300 rounded-full ${index === currentIndex
                                    ? 'bg-warm-sage w-8 h-3'
                                    : 'bg-soft-gray w-3 h-3 hover:bg-warm-gray'
                                }`}
                            aria-label={`Go to review ${index + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default ReviewsCarousel;
